import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  total: number
  itemsPerPage: number
  currentPage: number
  onPageChange: (page: number) => void
}

function ActivityPagination(props: Props) {
  const { total, itemsPerPage, currentPage, onPageChange } = props
  const intl = useIntl()

  const totalPages = Math.ceil(total / itemsPerPage)
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  const handlePrevious = useCallback(() => {
    if (!isFirstPage) {
      onPageChange(currentPage - 1)
    }
  }, [currentPage, isFirstPage, onPageChange])

  const handleNext = useCallback(() => {
    if (!isLastPage) {
      onPageChange(currentPage + 1)
    }
  }, [currentPage, isLastPage, onPageChange])

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, total)

  const pageLabelMessage = {
    id: 'activityPagination.page',
    defaultMessage: 'Page {current} of {total}',
    description: 'Pagination page indicator showing current page and total pages',
  }
  const pageLabel = intl.formatMessage(pageLabelMessage, { current: currentPage, total: totalPages })

  const showingLabelMessage = {
    id: 'activityPagination.showing',
    defaultMessage: 'Showing {start}-{end} of {total}',
    description: 'Pagination text showing range of items displayed',
  }
  const showingLabel = intl.formatMessage(showingLabelMessage, { start: startItem, end: endItem, total })

  if (total <= itemsPerPage) {
    return null
  }

  return (
    <Group direction="vertical" gap={12}>
      <Group direction="horizontal" horizontalAlign="center" verticalAlign="center" gap={16}>
        <IconButton
          kind="tertiary"
          onClick={handlePrevious}
          disabled={isFirstPage}
          aria-label={intl.formatMessage({
            id: '/G9QP7sbeG',
            defaultMessage: 'Previous page',
            description: 'Aria label for previous page button in loyalty activity pagination',
          })}
        >
          <LineAngleLeftIcon />
        </IconButton>
        <Caption variant="medium" colour="neutral-two">
          {pageLabel}
        </Caption>
        <IconButton
          kind="tertiary"
          onClick={handleNext}
          disabled={isLastPage}
          aria-label={intl.formatMessage({
            id: 'PzYKZG/+k7',
            defaultMessage: 'Next page',
            description: 'Aria label for next page button in loyalty activity pagination',
          })}
        >
          <LineAngleRightIcon />
        </IconButton>
      </Group>
      <Group direction="horizontal" horizontalAlign="center">
        <BodyText variant="small" colour="neutral-two">
          {showingLabel}
        </BodyText>
      </Group>
    </Group>
  )
}

export default ActivityPagination
